import { useState, useEffect } from 'react';
import { isBrowser } from 'ws-scripts/modules/environment';
import logger from 'ws-scripts/modules/logger';

const LOG_PREFIX = '[ws-locations-filters][useLocationsDataReady]';
const TIMEOUT_MS = 5000;
const LOCATIONS_DATA_READY_EVENT = 'DDC:LocationsDataReady';

/**
 * Hook to detect when ws-locations-data LocationsData is ready.
 * Listens for DDC:LocationsDataReady custom event dispatched by ws-locations-data.
 *
 * @returns {{ isReady: boolean, error: string | null }}
 */
export const useLocationsDataReady = () => {
	const [isReady, setIsReady] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (!isBrowser) {
			return () => {};
		}

		// Check if already available
		if (window.DDC?.LocationsData) {
			logger.debug(`${LOG_PREFIX} LocationsData already available`);
			setIsReady(true);
			return () => {};
		}

		// Listen for ready event
		const handleReady = () => {
			logger.debug(`${LOG_PREFIX} LocationsDataReady event received`);
			setIsReady(true);
			setError(null); // Clear any timeout error
		};

		window.addEventListener(LOCATIONS_DATA_READY_EVENT, handleReady);
		logger.debug(`${LOG_PREFIX} Listening for LocationsDataReady event`);

		// Timeout fallback
		const timeout = setTimeout(() => {
			if (!window.DDC?.LocationsData) {
				logger.error(
					`${LOG_PREFIX} LocationsData initialization timeout`
				);
				setError('Location service not available');
			}
		}, TIMEOUT_MS);

		// Cleanup
		return () => {
			window.removeEventListener(LOCATIONS_DATA_READY_EVENT, handleReady);
			clearTimeout(timeout);
		};
	}, []);

	return { isReady, error };
};
